import { useState, useContext, useEffect, useRef } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiBadge from "components/SuiBadge";

import Checkbox from "@mui/material/Checkbox";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import Settings from "examples/Icons/Settings";
import Cube from "examples/Icons/Cube";
import SpaceShip from "examples/Icons/SpaceShip";
import api from "services/api";

// Authentication layout components
import SignInLayout from "layouts/upstyled/SigninLayout";

import { AccountContext } from "context/account";
import { StoreContext } from "context/store";
import { useForm } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import {
  isPossiblePhoneNumber,
  formatPhoneNumberIntl,
  parsePhoneNumber,
} from "react-phone-number-input";

//import LetterLogo from "components/UpStyled/LetterLogo";
import UpStyledLogo from "components/UpStyled/UpStyledLogo";

import Prompt from "components/UpStyled/Prompt";
import PasswordChecklist from "react-password-checklist";
import toast from "react-hot-toast";
import qs from "query-string";
import { decodeToken } from "react-jwt";

import { accountTypeOptions, externalLinks } from "utils/data";

import SelectRoleCard from "pages/signup-new/components/SelectRoleCard";
import AccountInfo from "pages/signup-new/components/AccountInfo";

function SignUpNew() {
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [accountRole, setAccountRole] = useState("de");
  const [accountTags, setAccountTags] = useState([]);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleNext = (e) => {
    e.preventDefault();
    setActiveStep(activeStep + 1);
  };
  const handleBack = () => setActiveStep(activeStep - 1);

  const account = useContext(AccountContext);

  let location = useLocation();
  const navigate = useNavigate();

  const accountRef = useRef(null);

  const { handleSubmit, register, reset, setValue } = useForm();

  const url = window?.location?.href ? window.location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url) || {};
  let token = urlObj?.query.token;
  let tokenObj = null;
  try {
    tokenObj = decodeToken(token);
  } catch (e) {
    token = null;
  }

  let isReferMode = false; //if url contains user_email, then we will consider it is referenced by a registed user
  if (tokenObj && tokenObj.user_email) {
    isReferMode = true;
  }

  useEffect(() => {
    if (isReferMode) {
      reset({
        email: tokenObj?.user_email,
      });
      setAccountRole("de");
    } else {
      reset({
        email: urlObj?.query?.email,
      });
      if (urlObj?.query?.invite_code) {
        const myInviteCode = urlObj.query.invite_code.toUpperCase();
        setInviteCode(myInviteCode);
      }

      if (["de", "ma", "sp", "bu"].includes(urlObj?.query?.account_type)) {
        setAccountRole(urlObj.query.account_type);
      }
    }
  }, [url]);

  const submitRegister = async (data) => {
    console.log(data);
    const phoneNum = accountRef?.current?.phone;
    if (data["new_password"] != data["confirm_password"]) {
      return toast.error("The two passwords that you entered do not match!");
    }
    if (!isValidPassword) {
      return toast.error("Your password is invalid!");
    }

    if (phoneNum) {
      if (!isPossiblePhoneNumber(phoneNum)) {
        return toast.error("Not a valid phone number!");
      }
    }

    if (!accountRef?.current?.agreement) {
      return toast.error(
        "Please agree to our terms and privacy policy before register your account!"
      );
    }

    if (isReferMode) {
      const userConfig = {
        first_name: data["first_name_input"],
        last_name: data["last_name_input"],
        password: data["new_password"],
        email: tokenObj.user_email,
        account_type: accountRole == "ma" ? "ma" : "de",
        stores: [
          {
            name: data["business_name"],
            metadata: {
              business_phone: phoneNum,
            },
          },
        ],
        metadata: {
          user_source: {
            url: url,
            referrer: document.referrer,
          },
          role_tags: accountRole == "sp" ? accountTags || [] : [],
          account_role: accountRole,
          login_times: 0,
        },
      };
      const params = {
        token: token,
        user: userConfig,
      };
      try {
        await api.invites.accept(params);
        await account.handleLogin({
          email: tokenObj["user_email"],
          password: data["new_password"],
        });

        navigate("/edit-profile", { replace: true });
        toast.success("Welcome to UpStyled!");
      } catch (err) {
        let message = "Register failed!";
        const data = err?.response?.data;
        if (data?.type == "invalid_data" && data?.message != "") {
          message = data.message;
        }
        toast.error(message);
        console.log(err);
      }
    } else {
      const payload = {
        user: {
          first_name: data["first_name_input"],
          last_name: data["last_name_input"],
          password: data["new_password"],
          email: data["email"],
          account_type: accountRole == "ma" ? "ma" : "de",
          stores: [
            {
              name: data["business_name"],
              metadata: {
                business_phone: phoneNum,
              },

              // story: data["story"],
            },
          ],
          metadata: {
            user_source: {
              url: url,
              invite_code: inviteCode,
              referrer: document.referrer,
            },
            role_tags: accountRole == "sp" ? accountTags || [] : [],
            account_role: accountRole,
            login_times: 0,
          },
        },
      };

      try {
        await api.invitecode.accept(payload);

        toast.success("Welcome to UpStyled!");
        setSignupSuccess(true);
      } catch (err) {
        let message = "Register failed!";
        const data = err?.response?.data;
        if (data?.type == "invalid_data" && data?.message != "") {
          message = data.message;
        }
        toast.error(message);
        console.log(err);
      }
    }
  };
  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <SelectRoleCard
            sx={{ m: 2, width: 500 }}
            accountRole={accountRole}
            setAccountRole={setAccountRole}
            accountTags={accountTags}
            setAccountTags={setAccountTags}
            isReferMode={isReferMode}
          />
        );
      case 1:
        return (
          <AccountInfo
            sx={{ m: 2 }}
            ref={accountRef}
            register={register}
            reset={reset}
            setValue={setValue}
            setIsValidPassword={setIsValidPassword}
            accountType={accountRole}
          />
        );
      default:
        return null;
    }
  }
  const isLastStep = activeStep === 1;

  return (
    <SignInLayout>
      <Card sx={{ px: "40px", py: "12px", margin: "auto" }}>
        <SuiBox mb={1} mt={4} px={6} display="flex" justifyContent="center">
          <div>
            <UpStyledLogo sx={{ height: 60 }} />
          </div>
        </SuiBox>

        {signupSuccess ? (
          <SuiBox px={3} mb={4}>
            <SuiTypography
              variant="h6"
              fontWeight="medium"
              className="flex items-center"
              color="dark"
            >
              Please check your email
            </SuiTypography>
            <SuiTypography variant="button" color="text" fontWeight="regular">
              We’ve sent you an email to verify your email address.
              <br />
              Please check your Promotions folder or Spam folder if you haven’t received it after a
              few minutes.
            </SuiTypography>
          </SuiBox>
        ) : (
          <div>
            <SuiBox px={3}>
              <>
                <SuiBox mb={1}>
                  <SuiTypography
                    variant="h4"
                    fontWeight="bold"
                    color={"info"}
                    textGradient
                    textAlign="center"
                    sx={{ width: "100%" }}
                  >
                    Join Us today
                  </SuiTypography>
                </SuiBox>
              </>
            </SuiBox>
            <SuiBox
              component="form"
              onSubmit={handleSubmit(submitRegister)}
              sx={{ maxWidth: "560px" }}
            >
              {getStepContent(activeStep)}

              <SuiBox mt={1} textAlign="center">
                <SuiTypography variant="button" color="text" fontWeight="regular">
                  Already have an account?&nbsp;
                  <SuiTypography
                    component={Link}
                    to="/login"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign in
                  </SuiTypography>
                </SuiTypography>
              </SuiBox>

              <SuiBox mt={3} width="100%" display="flex" justifyContent="space-between">
                {activeStep === 0 ? (
                  <SuiBox />
                ) : (
                  <SuiButton variant="gradient" color="secondary" onClick={handleBack}>
                    back
                  </SuiButton>
                )}
                <SuiButton
                  variant="gradient"
                  color="dark"
                  onClick={!isLastStep ? handleNext : undefined}
                  type={isLastStep ? "submit" : "button"}
                >
                  {isLastStep ? "Create Account" : "next"}
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </div>
        )}
      </Card>
    </SignInLayout>
  );
}

export default SignUpNew;
